<template>
    <div class="mb-3">
        <GoogleMaps
            :title="null"
            :addressLabel="addressLabel"
            v-model:fullAddress="addressObject.fullAddress"
            v-model:street="addressObject.street"
            v-model:streetNumber="addressObject.streetNumber"
            v-model:unitNumber="addressObject.unitNumber"
            v-model:postalCode="addressObject.postalCode"
            v-model:city="addressObject.city"
            v-model:province="addressObject.province"
            :fieldValidator="this.fieldValidator"
            :isStepReadOnly="this.isStepReadOnly"
            :hasPrepopAddress="this.hasPrepopAddress"
        />
    </div>
</template>

<script>
import { ref, inject, onMounted } from "vue";

import GoogleMaps from '@/components/form/GoogleMaps.vue';

export default {
    name: 'AddressInput',
    emits: ["update:model-value", "input", "validateAddress"],
    components: { GoogleMaps },
    props: {
        //coming from input step fields .js => custom <component>
        name: {
            type: String,
            required: true,
        },
        addressLabel: {
            type: String,
            required: false,
            default: '',
        },
        value: {
            required: true,
        },
        fieldValidator: {
            type: Object,
            default: {},
        },
        isStepReadOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
        prepopAddress: {
            type: String,
            required: false,
            default: false,
        },
        addressProp: {
            type: Object,
            required: false,
            default: {},
        },
    },
    setup(props, { emit }) {
        const { app, updateApplicationField } = inject('app');
        const addressObject = ref({
            fullAddress: "",
            street: "",
            streetNumber: "",
            unitNumber: "",
            postalCode: "",
            city: "",
            province: "",
        });

        const hasPrepopAddress = ref(false);
        const localStorageAddressKey = 'comparehub-user-address';
        const localStorageAddressExpiryHours = 24;

        const extractCivicNumber = (addressString) => {
            const pattern = /^\d+(-\d+)?/;
            const match = addressString.match(pattern);
            return match ? match[0] : null;
        };

        const getAddressFromLocalStorage = () => {
            const now = new Date();
            let foundAddressInLocalStorage = false;

            const storedAddressData = JSON.parse(localStorage.getItem(localStorageAddressKey) ?? null);
            if (storedAddressData?.addressData && new Date(storedAddressData.expiry) > now) {
                for (const field of Object.keys(addressObject.value)) {
                    if (field in storedAddressData.addressData) {
                        foundAddressInLocalStorage = true;
                        addressObject.value[field] = storedAddressData.addressData[field];
                    }
                }
            }
            return foundAddressInLocalStorage;
        };

        const storeAddressInLocalStorage = () => {
            const now = new Date();
            const expirationTime = new Date(now.getTime() + (60 * 60 * 1000 * localStorageAddressExpiryHours));

            localStorage.removeItem(localStorageAddressKey);

            const storeNewAddressData = {
                addressData: addressObject.value,
                expiry: expirationTime,
            };
            localStorage.setItem(localStorageAddressKey, JSON.stringify(storeNewAddressData));
        };

        const validateAddress = () => {
            emit("validateAddress", addressObject.value);
            if (props.fieldValidator?.isValid) {
                storeAddressInLocalStorage();
            }
        };

        onMounted(() => {
            let civicNumber = null;
            let addressStreet = null;

            if (getAddressFromLocalStorage()) {
                hasPrepopAddress.value = true;
            } else {
                if (props.prepopAddress) {
                    hasPrepopAddress.value = true;
                    civicNumber = extractCivicNumber(props.prepopAddress);
                    addressStreet = civicNumber ? props.prepopAddress.replace(civicNumber, '').trim() : props.prepopAddress;
                }

                addressObject.value.streetNumber = props.addressProp?.streetNumber ?? civicNumber ?? null;
                addressObject.value.street = props.addressProp?.street ?? addressStreet;
                addressObject.value.city = props.addressProp?.city ?? app['city'] ?? null;
                addressObject.value.province = props.addressProp?.province ?? app['province'] ?? app['state'] ?? null;
                addressObject.value.postalCode = props.addressProp?.postalCode ?? app['postal_code'] ?? null;
            }
        });

        return {
            addressObject,
            hasPrepopAddress,
            updateApplicationField,
            validateAddress,
        };
    },
    computed: {
    },
    methods: {
    },
    watch: {
        addressObject: {
            deep: true,
            immediate: false,
            handler(val) {
                this.updateApplicationField(this.name, val);
                this.validateAddress();
            },
        },
    }
};
</script>
